import { ReferenceChip } from '@heidi/agents';
import {
  refTargetFrom,
  unclobberedAttr,
  type RefKind,
  type RefTarget,
  type RefTargetOf,
} from '@heidi/agents/contracts';
import { Children, isValidElement, type ReactNode } from 'react';

import { toReferenceState } from './chat-object-reference-hooks';
import {
  REFERENCE_KINDS,
  useOpenReference,
  type ReferenceFor,
  type ReferenceKind,
} from './chat-object-reference-kinds';

export const ResolveReference = <K extends RefKind>({
  target,
  labelHint,
  children,
}: {
  target: RefTargetOf<K>;
  labelHint?: string;
  children: (ref: ReferenceFor<K>) => ReactNode;
}): JSX.Element => {
  const { Resolve } = REFERENCE_KINDS[target.kind as K];
  return (
    <Resolve target={target} labelHint={labelHint}>
      {children}
    </Resolve>
  );
};

export const ChatObjectReference = ({
  target,
}: {
  target: RefTarget & { labelHint?: string };
}): JSX.Element => {
  const openReference = useOpenReference();
  const { open } = REFERENCE_KINDS[target.kind] as ReferenceKind<RefKind>;
  return (
    <ResolveReference target={target} labelHint={target.labelHint}>
      {(ref) => {
        const destination = open?.(target, ref);
        return (
          <ReferenceChip
            kind={target.kind}
            state={toReferenceState(
              ref,
              destination && (() => openReference(destination))
            )}
          />
        );
      }}
    </ResolveReference>
  );
};

type RefMarkdownElementProps = {
  children?: ReactNode;
  'data-kind'?: string;
  dataKind?: string;
  'data-id'?: string;
  dataId?: string;
  'data-session-id'?: string;
  dataSessionId?: string;
  'data-url'?: string;
  dataUrl?: string;
};

const targetFromAttrs = (props: RefMarkdownElementProps): RefTarget | null => {
  const kind = unclobberedAttr(props['data-kind'], props.dataKind);
  const id = unclobberedAttr(props['data-id'], props.dataId);
  const sessionId =
    unclobberedAttr(props['data-session-id'], props.dataSessionId) || undefined;
  const url = unclobberedAttr(props['data-url'], props.dataUrl) || undefined;
  return refTargetFrom(kind, id, sessionId, url) ?? refTargetFrom(kind, id, sessionId);
};

const textOf = (node: ReactNode): string =>
  Children.toArray(node)
    .map((child) =>
      typeof child === 'string' || typeof child === 'number'
        ? String(child)
        : isValidElement<{ children?: ReactNode }>(child)
          ? textOf(child.props.children)
          : ''
    )
    .join('');

export const RefMarkdownElement = (props: RefMarkdownElementProps): JSX.Element => {
  const target = targetFromAttrs(props);
  return target ? (
    <ChatObjectReference target={{ ...target, labelHint: textOf(props.children) }} />
  ) : (
    <>{props.children}</>
  );
};
